import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ProjectCard from "../components/projects/ProjectCard";
import ProjectDetails from "../components/projects/ProjectDetails";

interface Project {
  title: string;
  image?: string;
  description: string;
  tags: string[];
  markdownFile: string;
}

const projects: Project[] = [
  {
      title: "ERC-20 Copy Trader & Analyzer",
      image: "/projects/erc20.png",
      description: "Contains functionality for copy trading on Ethereum and wallet trade analysis. Contains a machine learning algorithm used to determine the future price of a given token.",
      tags: ["Typescript", "NestJS", "Next.js", "Python", "Machine Learning", "scikit-learn", "FastAPI", "ethers.js", "Ethereum", "Blockchain", "React", "GraphQL"],
      markdownFile: "/projects/erc20.md",
  },
  {
      title: "Fullstack-Loadgen",
      image: "/projects/fullstack.png",
      description: "Full stack demo application made using Go & React. CI/CD pipeline using GitHub actions for resource creation and deployment to Azure AKS.",
      tags: ["Go", "Gin", "React", "AKS", "Key Vault", "Terraform", "HCL", "Kubernetes", "Docker", "Nginx", "CI/CD", "GitHub Actions", "ACR"],
      markdownFile: "/projects/fullstack.md",
  },
  {
      title: "MC Kube",
      image: "/projects/mc-kube.png",
      description: "Kubernetes, mc-router, and nip.io for wildcard DNS mappings to run multiple servers",
      tags: ["Kubernetes", "nip.io", "mc-router"],
      markdownFile: "/projects/mc-kube.md",
  },
  {
      title: "Voxel Project",
      image: "/projects/voxels.png",
      description: "Optimized voxel engine using greedy meshing & frustum culling. POC Delaunay Triangulations for LOD and background details (not fully implemented).",
      tags: ["C++", "GLFW", "GLEW", "OpenGL", "Greedy Meshing", "Delaunay Triangulations"],
      markdownFile: "/projects/voxels.md",
  },
  {
      title: "Snow Shuttle",
      image: "/projects/snowshuttle.png",
      description: "Startup aiming to get more students to the mountains to go skiing. Growth to 150 users & DU Micogrant recipient of $1000.",
      tags: ["Django", "Python", "AWS", "RDS", "AWS Secrets Manager", "Expo", "React Native", "CI/CD", "GitHub Actions"],
      markdownFile: "/projects/snowshuttle.md",
  },
  {
      title: "Golfsnake",
      description: "I worked at Golfsnake for a little over a year before its closure. I was the primary developer during my time here and saw growth of 1500+ users. Designed and implemented a backend panel used to measure system health at a granular level.",
      image: "/projects/golfsnake.png",
      tags: ["TypeScript", "Next.js", "NestJS", "Nginx", "LetsEncrypt", "Web Scraping", "CI/CD", "GitLab Pipelines"],
      markdownFile: "/projects/golfsnake.md",
  },
  {
      title: "Pendulums",
      image: "/projects/pendulum.png",
      description: "OpenGL and C++ to programmatically generate thousands of double pendulums and pick the best ones to try to sell as NFT's. Didn't end up selling any...",
      tags: ["C++", "OpenGL"],
      markdownFile: "/projects/pendulum.md",
  },
  {
      title: "Wormhole",
      image: "/projects/wormhole.png",
      description: "Simple project, manual vertex generation of a Torus and rotate it fast.",
      tags: ["WebGL", "JavaScript"],
      markdownFile: "/projects/wormhole.md",
  },
  {
      title: "Trilandia",
      description: "Game made in Java when I was 12. Designed all the graphics, game made fully from scratch without an engine. LWJGL for rendering, dynamic tile lighting, cellular automata, midpoint displacement for terrain generation.",
      image: "/projects/trilandia.png",
      tags: ["Java", "OpenGL", "LWJGL"],
      markdownFile: "/projects/trilandia.md",
  },
  {
      title: "DS Call of Duty League",
      image: "/projects/codleague.png",
      description: "During COVID a bunch of buddies and I played a ton of COD. There was a point where we did tournaments and had several bracket stages, over 25 people playing. But everyone argued about stats, so I made a website where our admins would input stats and everyone could check on the website.",
      tags: ["Node.js", "Chart.js", "Bootstrap", "jQuery", "MySQL"],
      markdownFile: "/projects/codleague.md",
  },
  {
      title: "ETH Template",
      image: "/projects/eth_template.png",
      description: "Simple frontend website with Bootsstrap, meant as a template for some NFT.",
      tags: ["JavaScript", "Node.js", "jQuery", "Web3"],
      markdownFile: "/projects/eth_template.md",
  },
  {
      title: "Socket Painter",
      image: "/projects/socket.png",
      description: "Socket client and server, allows for drawing and chat.",
      tags: ["Sockets", "Java", "Swing"],
      markdownFile: "/projects/socket.md",
  },

  // Add more projects here
];

const Projects = () => {
  const { title } = useParams<{ title?: string }>();
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string>("");

  // Handle project selection based on the route
  useEffect(() => {
    if (title) {
      const project = projects.find(
        (p) => p.title.toLowerCase() === decodeURIComponent(title).toLowerCase()
      );
      if (project) {
        fetch(project.markdownFile)
          .then((res) => res.text())
          .then((markdown) => {
            setSelectedProject(project);
            setMarkdownContent(markdown);
          });
      } else {
        navigate("/projects"); // Redirect if project not found
      }
    } else {
      // Reset view when no title is in the URL
      setSelectedProject(null);
      setMarkdownContent("");
    }
  }, [title, navigate]);

  const handleCloseDetails = () => {
    navigate("/projects");
  };

  return (
    <div className="min-h-screen w-full py-4">
      <h2 className="text-3xl font-bold text-white text-center mb-5 pt-5 mt-20">
        Featured Projects
      </h2>
      <h3 className="text-lg text-white text-center">
        Click on any to see more details.
      </h3>
      <div className="mt-5">
        {selectedProject ? (
          <ProjectDetails
            title={selectedProject.title}
            image={selectedProject.image}
            markdownContent={markdownContent}
            onClose={handleCloseDetails}
          />
        ) : (
          <div className="flex flex-col items-center justify-start p-4 pt-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <ProjectCard
                key={index}
                title={project.title}
                image={project.image}
                description={project.description}
                tags={project.tags}
                onClick={() =>
                  navigate(`/projects/${encodeURIComponent(project.title)}`)
                }
              />
            ))}
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Projects;
